import React from 'react';
import { configService } from 'Services';
import {
    ALPHANUMERIC_REGEX,
    DIGITS_ONLY_REGEX,
    EMPTY_OR_UTC_DATE_REGEX,
    ONE_TO_150_ANY,
    TWELVE_TO_19_ANY,
    ZERO_TO_150_ANY
} from 'Utils';

const merchantIdColumn = {
    acceptableHeaders: ['identifier'],
    accessor: 'merchantId',
    required: true,
    validation: {
        tests: [
            {
                regex: ALPHANUMERIC_REGEX,
                message: 'Identifier must be alphanumeric.'
            }
        ]
    }
};

const commentColumn = {
    acceptableHeaders: ['comment'],
    accessor: 'comment',
    required: true,
    validation: {
        tests: [
            {
                regex: ZERO_TO_150_ANY,
                message: 'Comment must be between 0 and 150 characters.'
            }
        ]
    }
};

const startTimeColumn = {
    acceptableHeaders: ['starttime'], // token-service lowercases csv headers when validating
    accessor: 'startTime',
    required: true,
    validation: {
        tests: [
            {
                method: 'isDateValid',
                message: 'Start time must be valid.'
            },
            {
                regex: EMPTY_OR_UTC_DATE_REGEX,
                message: 'Start time must be in YYYY-MM-DD HH:MM format.'
            }
        ]
    }
};

const endTimeColumn = {
    acceptableHeaders: ['endtime'], // token-service lowercases csv headers when validating
    accessor: 'endTime',
    required: true,
    validation: {
        tests: [
            {
                method: 'isDateValid',
                message: 'End time must be valid.'
            },
            {
                regex: EMPTY_OR_UTC_DATE_REGEX,
                message: 'End time must be in YYYY-MM-DD HH:MM format.'
            }
        ]
    }
};

function getCsvColumns(
    listType: SingleListType | null,
    merchantSpecific: boolean,
    customListType?: CustomListObj | undefined
): BulkUploaderColumn[] {
    const { customListTypes = [] } = configService;
    const currentListType: any = customListTypes.find((list) => {
        return list.customListType === customListType?.listType;
    });

    let columns: BulkUploaderColumn[];
    switch (listType) {
        case 'GENERAL': {
            columns = [
                commentColumn,
                startTimeColumn,
                endTimeColumn,
                {
                    acceptableHeaders: ['value'],
                    accessor: 'value',
                    required: true,
                    validation: {
                        // alphanumeric (and underscore)
                        tests: [
                            currentListType?.validationDetails?.value,
                            {
                                method: 'checkHasPAN',
                                message: 'Cannot upload PAN to this list'
                            },
                            {
                                regex: ONE_TO_150_ANY,
                                message: 'Value must be between 1 - 150 characters.'
                            }
                        ]
                    }
                },
                {
                    acceptableHeaders: ['action'],
                    required: true,
                    accessor: 'action',
                    valuesToCount: ['remove']
                }
            ];

            break;
        }
        case 'PAN': {
            columns = [
                commentColumn,
                startTimeColumn,
                endTimeColumn,
                {
                    acceptableHeaders: ['primary account number'],
                    isTokenField: true,
                    required: true,
                    accessor: 'value',
                    validation: {
                        tests: [
                            currentListType?.validationDetails?.value,
                            {
                                regex: DIGITS_ONLY_REGEX,
                                message: 'PAN must be a digit.'
                            },
                            {
                                regex: TWELVE_TO_19_ANY,
                                message: 'PAN must be between 12-19 characters.'
                            }
                        ]
                    }
                },
                {
                    acceptableHeaders: ['action'],
                    required: true,
                    accessor: 'action',
                    isAddColumn: true,
                    valuesToCount: ['remove']
                }
            ];

            break;
        }
        case 'RANGE': {
            columns = [
                commentColumn,
                startTimeColumn,
                endTimeColumn,
                {
                    acceptableHeaders: ['to'],
                    accessor: 'to',
                    required: true,
                    validation: {
                        // alphanumeric (and underscore)
                        tests: [
                            currentListType?.validationDetails?.to,
                            {
                                method: 'checkHasPAN',
                                message: 'Cannot upload PAN to this list'
                            },
                            {
                                regex: DIGITS_ONLY_REGEX,
                                message: 'Value must be numeric'
                            }
                        ]
                    }
                },
                {
                    acceptableHeaders: ['from'],
                    accessor: 'from',
                    required: true,
                    validation: {
                        // alphanumeric (and underscore)
                        tests: [
                            currentListType?.validationDetails?.from,
                            {
                                method: 'checkHasPAN',
                                message: 'Cannot upload PAN to this list'
                            },
                            {
                                regex: DIGITS_ONLY_REGEX,
                                message: 'Value must be numeric'
                            }
                        ]
                    }
                },
                {
                    acceptableHeaders: ['action'],
                    required: true,
                    accessor: 'action',
                    valuesToCount: ['remove']
                }
            ];

            break;
        }
        // 'Unsupported List Type'
        default:
            return [];
    }
    return merchantSpecific ? columns.concat(merchantIdColumn) : columns;
}

function useListTypeBulkUploaderColumns(
    listType: SingleListType | null,
    merchantSpecific: boolean,
    customListType?: CustomListObj | undefined
) {
    const columns = React.useMemo(() => {
        return getCsvColumns(listType, merchantSpecific, customListType);
    }, [listType, merchantSpecific]);

    return columns;
}

export default useListTypeBulkUploaderColumns;
